import type { GPaginationProps } from '../types';

import { useCallback, useEffect, useRef, useState } from 'react';
import { GPaginationDirection } from '../types';

interface IUseGemXQlPaginationProps {
  resetKey: unknown;
  itemsPerPage: number;
}

interface IBuildPaginationPropsParams {
  totalRecords: number;
  isLoading?: boolean;
}

interface IUseGemXQlPaginationReturn {
  currentPage: number;
  offset: number;
  buildPagination: (params: IBuildPaginationPropsParams) => GPaginationProps;
}

export const useGemXQlPagination = ({
  resetKey,
  itemsPerPage,
}: IUseGemXQlPaginationProps): IUseGemXQlPaginationReturn => {
  const [currentPage, setCurrentPage] = useState(1);

  const previousResetKeyRef = useRef<unknown>();
  const shouldResetPagination =
    currentPage > 1 &&
    previousResetKeyRef.current !== undefined &&
    previousResetKeyRef.current !== resetKey;
  const effectivePage = shouldResetPagination ? 1 : currentPage;

  const offset = (effectivePage - 1) * itemsPerPage;

  const handlePageChange = useCallback(
    (totalPages: number) => (direction: GPaginationDirection) => {
      setCurrentPage((prevPage) => {
        const isNextPage = direction === GPaginationDirection.NEXT;
        const nextPage = isNextPage ? prevPage + 1 : prevPage - 1;
        return Math.min(Math.max(nextPage, 1), totalPages);
      });
    },
    [],
  );

  useEffect(() => {
    if (previousResetKeyRef.current !== resetKey) {
      previousResetKeyRef.current = resetKey;
      setCurrentPage(1);
    }
  }, [resetKey]);

  const buildPagination = useCallback(
    ({ totalRecords, isLoading }: IBuildPaginationPropsParams): GPaginationProps => {
      const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
      return {
        currentPage: effectivePage,
        totalPages,
        loading: isLoading,
        handlePageChange: handlePageChange(totalPages),
      };
    },
    [effectivePage, itemsPerPage, handlePageChange],
  );

  return { currentPage: effectivePage, offset, buildPagination };
};
